
import { Component, Vue, Watch } from 'vue-property-decorator';
import Themeable from 'vuetify/lib/mixins/themeable';
import { ProfileInterface, ProfileRepositoryInterface } from '@/services/Users-profile/interfaces';
import { WPXFormValue } from '@/components/templates/WPXForm';
import CreateFeedbackForm from '@/services/Dossier/feedback/forms/CreateFeedbackForm';
import DossierFeedbackRepositoryInterface, {
  CreateAgentFeedbackInterface,
  FeedbackCategoryInterface, FeedbackChatCommentInterface,
  FeedbackTypeInterface,
  feedbackEditorToolbar, FeedbackTicketCommentInterface, DossierFeedbackChatCommentInterface,
} from '@/services/Dossier/feedback/interfaces';
import DossierFeedbackReviewRepositoryInterface, {
  ReviewCategoryInterface,
} from '@/services/Dossier/review/interfaces';
import AddFeedbackChatCommentsDialog from '@/components/dialogs/dossier/feedback/AddFeedbackChatCommentsDialog.vue';
import { ProfileMapInterface } from '@/services/QC-V2/chat/interfaces';
import { VueEditor } from 'vue2-editor';
import AddFeedbackTicketCommentsDialog from '@/components/dialogs/dossier/feedback/AddFeedbackTicketCommentsDialog.vue';

@Component({
  components: {
    VueEditor,
    AddFeedbackChatCommentsDialog,
    AddFeedbackTicketCommentsDialog,
  },
  mixins: [
    Themeable,
  ],
  inject: [
    'dossierFeedbackRepository',
    'dossierFeedbackReviewRepository',
    'profileRepository',
  ],
})
export default class CreateAgentFeedback extends Vue {
  private loading: boolean = true;

  private userId!: string;
  private profile!: ProfileInterface;
  private feedbackCategories: FeedbackCategoryInterface[] = [];
  private feedbackTypes: FeedbackTypeInterface[] = [];
  private reviewCategories: ReviewCategoryInterface[] = [];
  private reviewCategoriesForSelects: ReviewCategoryInterface[][] = [];

  private dossierFeedbackRepository!: DossierFeedbackRepositoryInterface;
  private dossierFeedbackReviewRepository!: DossierFeedbackReviewRepositoryInterface;
  private profileRepository!: ProfileRepositoryInterface;

  private form: WPXFormValue<CreateFeedbackForm> = new WPXFormValue(CreateFeedbackForm);
  private forMonthMenu: boolean = false;

  private addFeedbackChatCommentDialogVisible: boolean = false;
  private addFeedbackTicketCommentDialogVisible: boolean = false;
  private selectedChatComments: FeedbackChatCommentInterface[] = [];
  private selectedTicketComments: FeedbackTicketCommentInterface[] = [];
  private profilesMap: ProfileMapInterface = {};

  private toolbar: unknown[] = feedbackEditorToolbar;

  private months: string[] = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  private async created(): Promise<void> {
    if (!this.$route.params.userId) {
      this.$errorDialog.show('User id not provided!');
      return;
    }

    if (this.$route.params.userId === this.$store.getters.id) {
      this.$errorDialog.show('You cannot add feedback for yourself!');
      return;
    }

    if (!this.$route.query.category) {
      this.$errorDialog.show('Category not provided!');
      return;
    }

    this.form.data.categoryId = Number(this.$route.query.category);
    this.userId = this.$route.params.userId;
    await this.initialize();
  }

  private async initialize(): Promise<void> {
    try {
      this.feedbackCategories = await this.dossierFeedbackRepository.getFeedbackCategories();
      this.feedbackTypes = await this.dossierFeedbackRepository.getFeedbackTypes();
      this.reviewCategories = await this.dossierFeedbackReviewRepository.getReviewCategories();
      this.reviewCategoriesForSelects.push(this.reviewCategories);
      this.assignFormData();
      this.profile = await this.profileRepository.getOneByUserId(this.userId);
      this.loading = false;
    } catch (e) {
      this.$errorDialog.show(e?.response?.data?.message || e.message);
    }
  }

  private async saveFeedback(): Promise<void> {
    try {
      const data: CreateAgentFeedbackInterface = {
        categoryId: this.form.data.categoryId,
        typeId: this.form.data.typeId,
        from: this.$store.getters.id,
        to: this.userId,
        title: this.form.data.title,
        reviews: this.form.data.reviews,
        forMonth: this.form.data.forMonth,
        chatComments: this.selectedChatComments.map((comment) => ({
          chatId: comment.chatId,
          reviewId: comment.reviewId,
          commentId: comment.commentId,
        })),
        ticketComments: this.selectedTicketComments.map((comment) => ({
          ticketNumber: comment.ticketNumber,
          reviewId: comment.reviewId,
          commentId: comment.commentId,
        })),
      };

      await this.dossierFeedbackRepository.createFeedback(data);
      this.$successDialog.show('Feedback Created Successfully');
      await this.goBack();
    } catch (e) {
      this.$errorDialog.show(e?.response?.data?.message || e.message);
    }
  }

  private addReview(index: number): void {
    this.form.data.reviews.push({
      categoryId: 0,
      body: '',
    });

    this.filterReviewCategories();
  }

  private removeReview(index: number): void {
    this.form.data.reviews.splice(index, 1);
    this.filterReviewCategories();
  }

  private filterReviewCategories(): void {
    this.form.data.reviews.forEach((_, index) => {
      this.reviewCategoriesForSelects[index] = this.reviewCategoriesForSelects
        .flat()
        .filter((category, categoryIndex, self) => self.indexOf(category) === categoryIndex)
        .filter((category) => category.id === this.form.data.reviews[index].categoryId
          || !this.form.data.reviews.map((formReview) => formReview.categoryId).includes(category.id))
        .sort((categoryA, categoryB) => categoryA.id - categoryB.id);
    });
  }

  private showChatCommentsDialog(): void {
    this.addFeedbackChatCommentDialogVisible = true;
  }

  private showTicketCommentsDialog(): void {
    this.addFeedbackTicketCommentDialogVisible = true;
  }

  private addChatComments(comments: FeedbackChatCommentInterface[], profilesMap: ProfileMapInterface): void {
    this.selectedChatComments = comments;
    this.profilesMap = profilesMap;
  }

  private addTicketComments(comments: FeedbackTicketCommentInterface[], profilesMap: ProfileMapInterface): void {
    this.selectedTicketComments = comments;
    this.profilesMap = profilesMap;
  }

  private getChatCommentLabel(comment: FeedbackChatCommentInterface): string {
    return `${comment.chatId}
    - ${this.profilesMap[comment.author].firstName} ${this.profilesMap[comment.author].lastName}: ${comment.text}`;
  }

  private getTicketCommentLabel(comment: FeedbackTicketCommentInterface): string {
    return `#${comment.ticketNumber}
    - ${this.profilesMap[comment.author].firstName} ${this.profilesMap[comment.author].lastName}: ${comment.text}`;
  }

  @Watch('form.data.forMonth')
  private assignFormData(): void {
    const date = this.form.data.forMonth ? this.form.data.forMonth : new Date();
    const currentDate = new Date(date);
    let typeName = 'Regular';
    if ([2, 8].includes(currentDate.getMonth())) {
      typeName = 'Attestation';
    }

    const feedbackType = this.feedbackTypes.find((type) => type.name === typeName);
    if (!feedbackType) {
      return;
    }

    this.form.data.typeId = feedbackType.id;
    this.generateTitle();
  }

  private generateTitle(): string {
    const date = this.form.data.forMonth ? this.form.data.forMonth : new Date();
    const currentDate = new Date(date);
    let title = `${this.months[currentDate.getMonth()]} ${currentDate.getFullYear()} Feedback`;
    if (this.form.data.typeId === 2) {
      title = `${title} (Attestation)`;
    }
    this.form.data.title = title;

    return title;
  }

  private checkFeedbackCategory(): boolean {
    const feedbackCategory = this.feedbackCategories.find((category) => category.id === this.form.data.categoryId);

    return !!feedbackCategory && feedbackCategory.name === 'QC Feedback';
  }

  private async goBack(): Promise<void> {
    await this.$router.push({ name: 'agent-profile', params: { userId: this.userId } });
  }
}
